import { useEffect, useMemo, useRef, useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import { applyServerErrors, serverErrorsOf } from 'components/forms/serverErrors'
import { equals } from 'ramda'
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    IconButton,
    Paper,
    Radio,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { DeleteOutline, DragIndicator, EditOutlined } from '@mui/icons-material'
import MDButton from '../MDButton'
import MarketingRemovalDialog from '../consent/MarketingRemovalDialog'
import {
    CONTACT_CONSENT_STATES,
    CONTACT_DETAIL_FLAGS,
    CONTACT_DETAIL_LABEL_OTHER,
    CONTACT_DETAIL_TYPES,
    CONTACT_RESTORE_LABEL,
    detailLabelChoiceOf,
    detailLabelFrom,
    detailLabelsOf,
    labelOf,
} from './contactLabels'
import { withFlag } from './contactLists'
import useDragReorder from './useDragReorder'
import DetailEditRefusal from './DetailEditRefusal'
import PendingBadge from './PendingBadge'

const EMPTY_DRAFT = {
    type: CONTACT_DETAIL_TYPES[0].value,
    label: '',
    other: '',
    value: '',
}

const ALL_FLAG_KEYS = CONTACT_DETAIL_FLAGS.map((flag) => flag.value)

const INTERACTIVE_SELECTOR = 'button, a, input, label, select, textarea, [role="button"]'

const ROW_ACTION_SX = {
    transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, color 150ms ease, opacity 150ms ease, transform 150ms ease',
    '&:hover:not(:disabled), &:focus-visible:not(:disabled)': {
        opacity: 1,
        transform: 'scale(1.1)',
        backgroundColor: 'action.hover',
        borderColor: 'primary.main',
    },
}

const ROW_ICON_BUTTON_SX = {
    border: '1px solid transparent',
    ...ROW_ACTION_SX,
}

const APPROVAL_LABEL_SX = {
    border: '1px solid transparent',
    borderRadius: 1,
    paddingInlineStart: 0.5,
    paddingInlineEnd: 1.625,
    transition: 'border-color 150ms ease',
    '&:hover:not(.Mui-disabled), &:focus-within:not(.Mui-disabled)': {
        borderColor: 'primary.main',
    },
    '&:hover:not(.Mui-disabled) .MuiSvgIcon-root, &:focus-within:not(.Mui-disabled) .MuiSvgIcon-root': {
        borderColor: 'primary.main !important',
        outlineStyle: 'solid',
        outlineWidth: '1px',
        outlineOffset: '-2px',
        outlineColor: 'primary.main',
    },
}

const APPROVAL_CHECKBOX_SX = {
    '&.Mui-focusVisible': {
        border: 'none !important',
    },
    '& .MuiSvgIcon-root': {
        borderColor: 'dark.main',
    },
    '&.Mui-disabled .MuiSvgIcon-root': {
        borderColor: 'text.disabled',
    },
}

const DRAFT_FIELD_OF = {
    name: 'label',
}

function retyped(draft, type) {
    const offered = detailLabelsOf(type).some((entry) => entry.value === draft.label)

    return {
        ...draft,
        type,
        label: offered ? draft.label : '',
    }
}

function draftOf(detail) {
    const { choice, other } = detailLabelChoiceOf(detail.type, detail.name)

    return {
        type: detail.type,
        label: choice,
        other,
        value: detail.value ?? '',
    }
}

function draftErrorsOf(fieldErrors) {
    return Object.entries(fieldErrors ?? {}).reduce((errors, [field, message]) => {
        const draftField = DRAFT_FIELD_OF[field] ?? field
        errors[draftField] = message
        return errors
    }, {})
}

function useDetailDraft({ emptyDraft, typeRef }) {
    const form = useFormikContext()
    const [editing, setEditing] = useState(null)
    const [focusRequests, setFocusRequests] = useState(0)

    useEffect(() => {
        if (focusRequests > 0) {
            typeRef.current?.focus()
        }
    }, [focusRequests])

    const draft = form.values

    const start = (id, loaded) => {
        setEditing({
            id,
            loaded,
        })
        form.resetForm({ values: loaded })
    }

    const reset = () => {
        setEditing(null)
        form.resetForm({
            values: {
                ...emptyDraft,
                type: draft.type,
            },
        })
        setFocusRequests((count) => count + 1)
    }

    const returnRefused = ({ entered, editingId, loaded, fieldErrors }) => {
        setEditing(editingId == null
            ? null
            : {
                id: editingId,
                loaded,
            })
        form.setValues(entered, false)
        applyServerErrors(form, draftErrorsOf(fieldErrors), entered)
    }

    return {
        draft,
        setDraft: (next) => form.setValues(next, false),
        errors: serverErrorsOf(form.status, form.values),
        editingId: editing?.id ?? null,
        loaded: editing?.loaded ?? null,
        changed: editing != null && !equals(draft, editing.loaded),
        start,
        reset,
        returnRefused,
    }
}

const SHAREABLE_TYPES = ['email', 'phone', 'whatsapp']

function flagsOfferedOn(type, sectionFlags) {
    return sectionFlags.filter((key) => {
        if (key === 'shareable') {
            return SHAREABLE_TYPES.includes(type)
        }

        return type === 'email'
    })
}

function DetailFlags({ detail, offered, disabled, onFlagChange }) {
    const shownFlags = offered
        .map((key) => CONTACT_DETAIL_FLAGS.find((flag) => flag.value === key))
        .filter((flag) => flag != null)

    if (shownFlags.length === 0) {
        return null
    }

    const change = (form, key, wanted) => {
        const flags = withFlag(form.values.flags, key, wanted)
        form.setFieldValue('flags', flags, false)

        Promise.resolve(onFlagChange?.(detail.id, key, wanted))
            .catch(() => {
                form.resetForm()
            })
    }

    return (
        <Formik
            initialValues={{ flags: detail.flags ?? [] }}
            enableReinitialize={true}
            onSubmit={() => {}}
        >
            {(form) => (
                <Stack direction="row" alignItems="center" data-testid="detail-flags">
                    <Typography variant="body2" sx={{ marginInlineEnd: 1 }}>מאושר:</Typography>
                    {shownFlags.map((flag) => (
                        <Tooltip key={flag.value} title={flag.tooltip}>
                            <Box
                                component="span"
                                data-testid={`detail-flag-label-${flag.value}`}
                                sx={{ display: 'inline-flex' }}
                            >
                                <FormControlLabel
                                    label={flag.label}
                                    sx={APPROVAL_LABEL_SX}
                                    control={
                                        <Checkbox
                                            name={flag.value}
                                            slotProps={{ input: { 'data-testid': `detail-flag-${flag.value}` } }}
                                            sx={APPROVAL_CHECKBOX_SX}
                                            checked={form.values.flags.includes(flag.value)}
                                            disabled={disabled}
                                            onChange={(event) => change(form, flag.value, event.target.checked)}
                                        />
                                    }
                                />
                            </Box>
                        </Tooltip>
                    ))}
                </Stack>
            )}
        </Formik>
    )
}

export default function ContactDetailsSection({ fixedType, types, ...props }) {
    const firstOfTypes = types?.[0]
    const emptyDraft = {
        ...EMPTY_DRAFT,
        type: fixedType ?? firstOfTypes ?? EMPTY_DRAFT.type,
    }

    return (
        <Formik
            initialValues={emptyDraft}
            onSubmit={() => {}}
            validateOnChange={false}
            validateOnBlur={false}
        >
            <DetailsSectionBody
                {...props}
                fixedType={fixedType}
                types={types}
                emptyDraft={emptyDraft}
            />
        </Formik>
    )
}

function DetailsSectionBody({
    details = [],
    onAdd,
    onUpdate,
    onRemove,
    onRemoveMarketing,
    onReorder,
    onFlagChange,
    flags = ALL_FLAG_KEYS,
    onRestore,
    onConfirmRemoval,
    refusals = {},
    fixedType,
    types,
    emptyDraft,
    reorderable: reorderableProp = true,
    selection,
    readOnly = false,
    disabled = false,
    busy = false,
    emptyMessage = 'לא הוגדרו פרטי קשר',
}) {
    const typeRef = useRef(null)
    const editor = useDetailDraft({
        emptyDraft,
        typeRef,
    })
    const offeredTypes = useMemo(
        () => CONTACT_DETAIL_TYPES.filter((type) => types == null || types.includes(type.value)),
        [types],
    )
    const shown = useMemo(
        () => {
            const ofFixedType = fixedType == null ? details : details.filter((detail) => detail.type === fixedType)

            return types == null ? ofFixedType : ofFixedType.filter((detail) => types.includes(detail.type))
        },
        [details, fixedType, types],
    )
    const { draft, setDraft } = editor
    const [refusal, setRefusal] = useState(null)
    const [removingMarketing, setRemovingMarketing] = useState(null)

    const locked = disabled || busy
    const draggable = reorderableProp && !locked && editor.editingId == null
    const { sorted, rowProps } = useDragReorder({
        rows: shown,
        reorderable: draggable,
        onReorder,
    })

    const dragPropsOf = (detail) => {
        const props = rowProps(detail.id)

        if (detail.storing !== true) {
            return props
        }

        return {
            ...props,
            draggable: false,
            onDragStart: undefined,
            onDrop: undefined,
        }
    }

    const submit = () => {
        const entered = draft
        const editingId = editor.editingId
        const loaded = editor.loaded
        const name = detailLabelFrom(draft.type, draft.label, draft.other)
        const input = {
            type: draft.type,
            name,
            value: draft.value,
        }

        const outcome = editingId == null
            ? onAdd?.(input)
            : onUpdate?.({
                id: editingId,
                ...input,
            })

        editor.reset()

        Promise.resolve(outcome)
            .then((result) => {
                if (result?.refused == null) {
                    return
                }

                editor.returnRefused({
                    entered,
                    editingId,
                    loaded,
                    fieldErrors: result.fieldErrors,
                })
            })
    }

    const edit = (detail) => {
        if (detail.consent === 'unsubscribed') {
            setRefusal('unsubscribed')
            return
        }

        if ((detail.flags ?? []).includes('marketing')) {
            setRefusal('marketing')
            return
        }

        editor.start(detail.id, draftOf(detail))
    }

    const remove = (detail) => {
        const holdsMarketing = (detail.flags ?? []).includes('marketing')

        if (holdsMarketing && onRemoveMarketing != null) {
            setRemovingMarketing(detail)
            return
        }

        onRemove?.(detail.id)
    }

    const confirmMarketingRemoval = () => {
        const detail = removingMarketing
        setRemovingMarketing(null)
        onRemoveMarketing?.(detail.id)
    }

    return (
        <Stack
            spacing={2}
            data-testid="contact-details-section"
            aria-busy={busy}
        >
            <MarketingRemovalDialog
                open={removingMarketing != null}
                detail={removingMarketing}
                onConfirm={confirmMarketingRemoval}
                onDecline={() => setRemovingMarketing(null)}
            />

            {sorted.length === 0 && (
                <Alert severity="info" data-testid="contact-details-empty">{emptyMessage}</Alert>
            )}

            {sorted.map((detail) => {
                const selected = selection?.selectedIds.includes(detail.id) === true
                const SelectionControl = selection?.kind === 'radio' ? Radio : Checkbox
                const selectable = selection?.selectableIds.includes(detail.id) === true
                const awaitingRemoval = detail.removed === true
                const selectsOnRowClick = selection != null && selectable && !locked && !awaitingRemoval
                const storing = detail.storing === true
                const rowLocked = locked || storing
                const selectRow = (event) => {
                    const onControl = event.target.closest(INTERACTIVE_SELECTOR) != null

                    if (!onControl) {
                        selection.onSelect(detail.id)
                    }
                }

                return (
                    <Paper
                        key={detail.id}
                        variant="outlined"
                        data-testid={`contact-detail-${detail.id}`}
                        data-value={detail.value ?? ''}
                        data-selected={selection == null ? undefined : String(selected)}
                        data-removed={detail.removed === true ? 'true' : undefined}
                        onClick={selectsOnRowClick ? selectRow : undefined}
                        {...dragPropsOf(detail)}
                        sx={{
                            padding: 1,
                            ...(selectsOnRowClick && { cursor: 'pointer' }),
                            ...(selected && {
                                backgroundColor: 'action.selected',
                                borderColor: 'primary.main',
                            }),
                            ...(detail.removed === true && { opacity: 0.6 }),
                        }}
                    >
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            alignItems="center"
                        >
                            <Stack
                                direction="row"
                                gap={1}
                                alignItems="center"
                                sx={{
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                }}
                            >
                                {selection != null && (
                                    <SelectionControl
                                        checked={selected}
                                        disabled={locked || awaitingRemoval || !selectable}
                                        onChange={() => selection.onSelect(detail.id)}
                                        slotProps={{ input: { 'aria-label': 'בחירה', 'data-testid': 'detail-choice' } }}
                                    />
                                )}

                                {reorderableProp && (
                                    <DragIndicator
                                        fontSize="small"
                                        color={draggable ? 'action' : 'disabled'}
                                        sx={{ cursor: draggable ? 'grab' : 'default' }}
                                    />
                                )}

                                <Typography variant="button" sx={{ minWidth: '5rem' }}>
                                    {labelOf(CONTACT_DETAIL_TYPES, detail.type)}
                                </Typography>

                                <Typography variant="body2" sx={{ minWidth: '9rem' }}>{detail.name}</Typography>

                                <Typography variant="body1" sx={{ flexGrow: 1 }} data-testid="detail-value">{detail.value}</Typography>
                            </Stack>

                            <Stack
                                direction="row"
                                gap={1}
                                alignItems="center"
                                sx={{
                                    flexShrink: 0,
                                    marginInlineStart: 'auto',
                                }}
                            >
                                {detail.consent === 'unsubscribed' && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        data-testid="contact-detail-unsubscribed"
                                        label={labelOf(CONTACT_CONSENT_STATES, detail.consent)}
                                    />
                                )}

                                {detail.consent != null && detail.consent !== 'unsubscribed' && labelOf(CONTACT_CONSENT_STATES, detail.consent) !== '' && (
                                    <Typography variant="caption" color="text.secondary">
                                        {labelOf(CONTACT_CONSENT_STATES, detail.consent)}
                                    </Typography>
                                )}

                                <DetailFlags
                                    detail={detail}
                                    offered={flagsOfferedOn(detail.type, flags)}
                                    disabled={rowLocked || readOnly || awaitingRemoval}
                                    onFlagChange={onFlagChange}
                                />

                                {storing && (
                                    <PendingBadge testId="contact-detail-pending" />
                                )}
                                {detail.removed === true && (
                                    <>
                                        <MDButton
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            disabled={rowLocked}
                                            data-testid="detail-confirm-removal"
                                            onClick={() => onConfirmRemoval?.(detail.id)}
                                            sx={ROW_ACTION_SX}
                                        >
                                            אישור מחיקה
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            disabled={rowLocked}
                                            data-testid="detail-restore"
                                            onClick={() => onRestore?.(detail.id)}
                                            sx={ROW_ACTION_SX}
                                        >
                                            {CONTACT_RESTORE_LABEL}
                                        </MDButton>
                                    </>
                                )}

                                {detail.removed !== true && !readOnly && (
                                    <>
                                        <IconButton
                                            aria-label="עריכה"
                                            data-testid="detail-edit"
                                            disabled={rowLocked || (editor.changed && editor.editingId !== detail.id)}
                                            onClick={() => edit(detail)}
                                            sx={ROW_ICON_BUTTON_SX}
                                        >
                                            <EditOutlined fontSize="small" />
                                        </IconButton>

                                        <IconButton
                                            aria-label="הסרה"
                                            data-testid="detail-remove"
                                            disabled={rowLocked || editor.editingId === detail.id}
                                            onClick={() => remove(detail)}
                                            sx={ROW_ICON_BUTTON_SX}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    </>
                                )}
                            </Stack>

                            {refusals[detail.id] != null && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    role="alert"
                                    data-testid="detail-refusal"
                                    sx={{ flexBasis: '100%' }}
                                >
                                    {refusals[detail.id]}
                                </Typography>
                            )}
                        </Stack>
                    </Paper>
                )
            })}

            {!readOnly && (
                <Stack
                    direction={{
                        xs: 'column',
                        lg: 'row',
                    }}
                    spacing={1}
                    alignItems={{ lg: 'center' }}
                >
                    <TextField
                        id="contact-detail-draft-type"
                        inputRef={typeRef}
                        label="סוג"
                        select
                        disabled={locked || editor.editingId != null || fixedType != null}
                        value={draft.type}
                        onChange={(event) => setDraft(retyped(draft, event.target.value))}
                        error={editor.errors.type != null}
                        helperText={editor.errors.type}
                        slotProps={{
                            select: { native: true },
                            htmlInput: { 'data-testid': 'detail-draft-type' },
                            formHelperText: { 'data-testid': 'detail-draft-error-type' },
                        }}
                        sx={{ minWidth: '11rem' }}
                    >
                        {offeredTypes.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                    </TextField>

                    <TextField
                        id="contact-detail-draft-name"
                        label="תוית"
                        select
                        disabled={locked}
                        value={draft.label}
                        onChange={(event) => setDraft({
                            ...draft,
                            label: event.target.value,
                        })}
                        error={editor.errors.label != null}
                        helperText={editor.errors.label}
                        slotProps={{
                            select: { native: true },
                            htmlInput: { 'data-testid': 'detail-draft-label' },
                            formHelperText: { 'data-testid': 'detail-draft-error-label' },
                        }}
                        sx={{ minWidth: '9rem' }}
                    >
                        <option value="" />
                        {detailLabelsOf(draft.type).map((label) => (
                            <option key={label.value} value={label.value}>{label.label}</option>
                        ))}
                    </TextField>

                    {draft.label === CONTACT_DETAIL_LABEL_OTHER && (
                        <TextField
                            id="contact-detail-draft-other"
                            label="תוית אחרת"
                            disabled={locked}
                            value={draft.other}
                            onChange={(event) => setDraft({
                                ...draft,
                                other: event.target.value,
                            })}
                        />
                    )}

                    <TextField
                        id="contact-detail-draft-value"
                        label="ערך"
                        error={editor.errors.value != null}
                        helperText={editor.errors.value}
                        slotProps={{
                            htmlInput: { 'data-testid': 'detail-draft-value' },
                            formHelperText: { 'data-testid': 'detail-draft-error-value' },
                        }}
                        disabled={locked}
                        value={draft.value}
                        onChange={(event) => setDraft({
                            ...draft,
                            value: event.target.value,
                        })}
                        sx={{ flexGrow: 1 }}
                    />

                    <MDButton
                        variant="outlined"
                        color="info"
                        disabled={locked}
                        data-testid="detail-draft-submit"
                        onClick={submit}
                    >
                        {editor.editingId == null ? 'הוספה' : 'עדכון'}
                    </MDButton>

                    {editor.editingId != null && (
                        <MDButton
                            variant="outlined"
                            color="secondary"
                            onClick={editor.reset}
                        >
                            ביטול
                        </MDButton>
                    )}
                </Stack>
            )}

            <DetailEditRefusal
                reason={refusal}
                onClose={() => setRefusal(null)}
            />
        </Stack>
    )
}
